/**
 * Workshop-delivery schedule status — derived, UTC-safe.
 *
 * A workshop delivery's real lifecycle (Not Scheduled → Scheduled → In-Progress → Complete) is not a
 * column: `CampaignWorkshopMeeting.status` only records the *booking* outcome
 * (`none`/`scheduling`/`scheduled`/`failed`/`cancelled`), and `CampaignDelivery.status` is admin-set.
 * The lifecycle the reporting pages need is a function of the Webex meeting's booking state plus its
 * start/end **instants** compared to now.
 *
 * All meeting times are stored as ISO-8601 UTC (the prep panel calls `toISOString()` before saving) with
 * the wall-clock IANA zone kept separately in `timezone`. So a single UTC comparison against
 * `Date.now()` (also UTC epoch ms) is correct regardless of the SE's or the viewer's local zone — no
 * zone math is needed here, which is the whole point of storing UTC.
 *
 * Pure module: no React, no fetching, `now` injectable for tests.
 */
import type { CampaignWorkshopMeeting } from './campaigns';

export type WorkshopScheduleStatus = 'not_scheduled' | 'scheduled' | 'in_progress' | 'complete';

/** Canonical order + labels for the four workshop schedule states. */
export const WORKSHOP_SCHEDULE_STATUSES: { value: WorkshopScheduleStatus; label: string }[] = [
  { value: 'not_scheduled', label: 'Not Scheduled' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'complete', label: 'Complete' },
];

const SCHEDULE_LABEL = new Map<WorkshopScheduleStatus, string>(
  WORKSHOP_SCHEDULE_STATUSES.map((s) => [s.value, s.label]),
);

export function workshopScheduleStatusLabel(status: WorkshopScheduleStatus): string {
  return SCHEDULE_LABEL.get(status) ?? status;
}

/** Parse an ISO-8601 UTC instant to epoch ms, or null when absent/unparseable. */
function parseMs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

/** The meeting's start instant (epoch ms, UTC), or null. */
export function meetingStartMs(m: CampaignWorkshopMeeting | null | undefined): number | null {
  return parseMs(m?.start_time);
}

/**
 * The meeting's end instant (epoch ms, UTC), or null. Falls back to `start + duration_minutes` when
 * `end_time` was not persisted, so a meeting booked with only a duration still resolves an end.
 */
export function meetingEndMs(m: CampaignWorkshopMeeting | null | undefined): number | null {
  const explicit = parseMs(m?.end_time);
  if (explicit != null) return explicit;
  const start = meetingStartMs(m);
  if (start != null && m?.duration_minutes) return start + m.duration_minutes * 60_000;
  return null;
}

/**
 * Derive the four-state workshop lifecycle from a meeting row and the current instant.
 *
 *  - **Not Scheduled** — no meeting, or a booking that never became a live meeting: any status other
 *    than `scheduled`, or a `scheduled` row with no `webex_meeting_id`/`start_time` (e.g. a
 *    still-`scheduling` order, a `failed`/`cancelled` booking, or the default `none`).
 *  - **Scheduled** — a live meeting whose start is still in the future.
 *  - **In-Progress** — start has passed and (a known) end has not, i.e. the window is open. When no end
 *    can be resolved we treat a started meeting as In-Progress rather than guessing Complete.
 *  - **Complete** — a resolvable end has passed.
 */
export function workshopScheduleStatus(
  m: CampaignWorkshopMeeting | null | undefined,
  now: number = Date.now(),
): WorkshopScheduleStatus {
  if (!m || m.status !== 'scheduled' || !m.webex_meeting_id) return 'not_scheduled';
  const start = meetingStartMs(m);
  if (start == null) return 'not_scheduled';
  if (now < start) return 'scheduled';
  const end = meetingEndMs(m);
  if (end != null && now >= end) return 'complete';
  // Started, and either the window is open or we can't resolve an end — it is under way, not done.
  return 'in_progress';
}

/** Lifecycle ordering, so a delivery with several meetings resolves to its most-advanced state. */
const STAGE_RANK: Record<WorkshopScheduleStatus, number> = {
  not_scheduled: 0,
  scheduled: 1,
  in_progress: 2,
  complete: 3,
};

/**
 * A delivery's single representative workshop status from its meeting rows: the most-advanced state
 * across them (Complete > In-Progress > Scheduled > Not Scheduled). A delivery with no meetings is
 * `not_scheduled`. This is the per-delivery reduction the pipeline-by-workshop-status table needs.
 */
export function deliveryWorkshopStatus(
  meetings: CampaignWorkshopMeeting[],
  now: number = Date.now(),
): WorkshopScheduleStatus {
  let best: WorkshopScheduleStatus = 'not_scheduled';
  for (const m of meetings) {
    const s = workshopScheduleStatus(m, now);
    if (STAGE_RANK[s] > STAGE_RANK[best]) best = s;
  }
  return best;
}

/**
 * Count meetings in each schedule state, in canonical order — the workshop status donut/bar series.
 * `{ label, value }` rows, empty states kept at 0. When a delivery has more than one meeting row the
 * caller should pre-reduce to the delivery's representative meeting; this counts whatever rows it is
 * given.
 */
export function workshopStatusDistribution(
  meetings: CampaignWorkshopMeeting[],
  now: number = Date.now(),
): { label: string; value: number }[] {
  const counts = new Map<WorkshopScheduleStatus, number>(
    WORKSHOP_SCHEDULE_STATUSES.map((s) => [s.value, 0]),
  );
  for (const m of meetings) {
    const status = workshopScheduleStatus(m, now);
    counts.set(status, (counts.get(status) ?? 0) + 1);
  }
  return WORKSHOP_SCHEDULE_STATUSES.map((s) => ({ label: s.label, value: counts.get(s.value) ?? 0 }));
}
